import { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import {
  Sparkles,
  Paperclip,
  Mic,
  MicOff,
  Send,
  Loader2,
  Globe,
  Plus,
  Search,
  Bell,
  Pencil,
  Check,
  X,
  Archive,
  FolderPlus,
  Folder,
  MoreVertical,
  Copy,
  Trash2,
  MessageSquarePlus,
  ChevronRight,
  ChevronDown,
  Star,
  Menu,
  // Fast action icons
  CheckSquare,
  Calendar,
  Mail,
  Phone,
  MessageCircle,
  StickyNote,
} from 'lucide-react';
import { useAppShell, type DrawerReorderEvent } from '../layouts/AppShell';
import { useHomeDeepLink, buildHomeUrl, useTranslation } from '../hooks';
import { useAuth } from '../contexts/AuthContext';
import { PrimaryAction, EntityAvatar } from '../components/common/base';
import { STANDARD_FAST_ACTIONS, type ActionType } from '../types/row.types';
// Shared services - RE-ENABLED after fixing circular dependencies
import { useChatStore } from '@business-assistant/shared/services';
import { aiFunnelClient, checkAiHealth } from '@business-assistant/shared/services';
import { whisperClient } from '@business-assistant/shared/services';
import { webSearchService } from '@business-assistant/shared/services';
import { fileStorageService } from '@business-assistant/shared/services';

import type { AiChatMessage, SearchResponse, ChatAttachment } from '@business-assistant/shared/services';
// Alias for backward compatibility
type GroqMessage = AiChatMessage;
import type { ChatMessage } from '@business-assistant/shared';

// AI Tools - ReAct pattern for tool calling
import {
  parseToolCall,
  requiresConfirmation,
  createPendingConfirmation,
  executeToolCall,
  initializeTools,
  isConfirmation,
  isCancellation,
} from '@business-assistant/shared/ai/tools';

// AI Agents - Initialize all Marta agents in the registry
import { initializeAgents } from '@business-assistant/shared/ai/agents';

// AI Router - Intelligent routing to AI providers/models
import { getAIRouter, type AIRoutingResult } from '@business-assistant/shared/ai/router';
import {
  buildSystemPrompt,
  buildSystemPromptWithMemory,
  createToolResultInjection,
  createConfirmationRequest,
  createCancellationMessage,
} from '@business-assistant/shared/ai/prompts';

// AI Memory - 4-Layer Architecture
import {
  memoryService,
  recordPattern,
  recordCorrection,
  submitFeedbackApi,
  type AIPatternType,
} from '@business-assistant/shared/ai/memory';

// Feedback Survey
import {
  FeedbackSurvey,
  incrementChatCount,
  incrementMessageCount,
  recordFeedbackGiven,
  shouldShowFeedback,
  type FeedbackData,
} from '@business-assistant/shared/components';

// AI Runner - New approach: fetch data from DB BEFORE calling the LLM
// Lazy-loaded AI runner module
let aiRunnerModule: typeof import('@business-assistant/shared/ai/runner') | null = null;

async function getAIRunner() {
  if (!aiRunnerModule) {
    aiRunnerModule = await import('@business-assistant/shared/ai/runner');
  }
  return aiRunnerModule;
}

// Backend Orchestrator - Cloud AI for intent detection (privacy-first)
// API keys secure on backend, data sanitized before cloud
import { orchestrateMessage as backendOrchestrate } from '@business-assistant/shared/ai/orchestrator/backendClient';
// Chat components - RE-ENABLED after fixing circular dependencies
import { ChatMessageBubble } from '../components/chat/ChatMessageBubble';
import { MessageDetailPanel } from '../components/chat/MessageDetailPanel';
import Markdown from 'react-markdown';

/**
 * HomeView - Chat interface stile ChatGPT
 *
 * Layout:
 * - Main: Chat content with header (editable title + actions)
 * - Sidebar: Chat history with header (Cronologia + search/folder actions)
 */
export function HomeView() {
  const { t } = useTranslation();
  const { user } = useAuth();

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const recorderRef = useRef<ReturnType<typeof whisperClient.createAudioRecorder> | null>(null);
  const recordingTimerRef = useRef<number | null>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);

  const { showToast, setDrawerSectionsOverride, setOnDrawerReorder, barsHidden } = useAppShell();

  // URL-based navigation for deep linking
  const {
    chatId,
    messageId,
    isMessageModalOpen,
    isNewChatScreen,
    navigateToChat,
    navigateToNewChat,
    openMessageModal,
    closeMessageModal,
  } = useHomeDeepLink();

  const {
    sessions,
    folders,
    initialized: chatInitialized,
    loadData: loadChatData,
    createSession,
    addMessage,
    updateSessionTitle,
    deleteSession,
    archiveSession,
    unarchiveSession,
    duplicateSession,
    moveSessionToFolder,
    createFolder,
    renameFolder,
    deleteFolder,
    setStreamingText,
    setStatus,
    setError,
    streamingText,
    setActiveSession,
    pendingConfirmation,
    setPendingConfirmation,
    toolCallCount,
    incrementToolCallCount,
    resetToolCallCount,
  } = useChatStore();

  const [inputText, setInputText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [webSearchMode, setWebSearchMode] = useState(false);
  const [webSearchPrompt, setWebSearchPrompt] = useState('');
  const [groqAvailable, setGroqAvailable] = useState<boolean | null>(null);
  const [whisperAvailable, setWhisperAvailable] = useState<boolean | null>(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [sidebarSearchQuery, setSidebarSearchQuery] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState<ChatAttachment[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [chatMenuOpen, setChatMenuOpen] = useState<string | null>(null);
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renamingChatId, setRenamingChatId] = useState<string | null>(null);
  const [renamingChatTitle, setRenamingChatTitle] = useState('');

  // Custom order for chat sessions (persisted in localStorage)
  const [chatOrder, setChatOrder] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('chat-order');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // State for handling non-existent chat
  const [chatNotFound, setChatNotFound] = useState(false);
  const [chatNotFoundId, setChatNotFoundId] = useState<string | null>(null);

  // Feedback survey state
  const [showFeedback, setShowFeedback] = useState<'global' | 'chat_specific' | null>(null);
  const feedbackDismissedRef = useRef(false);

  // Derive active session from URL chatId
  const activeSession = useMemo(() =>
    sessions.find(s => s.id === chatId),
    [sessions, chatId]
  );
  const messages = activeSession?.messages || [];

  // Force load chat sessions on mount - always fetch fresh data
  useEffect(() => {
    console.log('[HomeView] Mounting, forcing chat data load...');
    loadChatData?.();
  }, []);

  // Load messages when chatId changes - ONLY after data is initialized
  useEffect(() => {
    if (!chatInitialized) {
      // Data not loaded yet, wait
      return;
    }

    if (chatId) {
      // Check if the chat exists in our loaded sessions
      const sessionExists = sessions.some(s => s.id === chatId);

      if (sessionExists) {
        // Chat exists - load its messages
        setChatNotFound(false);
        setChatNotFoundId(null);
        setActiveSession(chatId);
      } else {
        // Chat doesn't exist - show "not found" state
        console.log('[HomeView] Chat not found:', chatId);
        setChatNotFound(true);
        setChatNotFoundId(chatId);
      }
    } else {
      // No chatId in URL - reset not found state
      setChatNotFound(false);
      setChatNotFoundId(null);
    }
  }, [chatId, chatInitialized, sessions, setActiveSession]);

  // Track new chats and check if feedback should be shown
  useEffect(() => {
    if (chatId && chatInitialized && !feedbackDismissedRef.current) {
      // When a new chat is started
      const isNewChat = !sessions.some(s => s.id === chatId);
      if (isNewChat) {
        incrementChatCount();
      }

      // Check if we should show feedback
      const feedbackType = shouldShowFeedback(messages.length);
      if (feedbackType && !showFeedback) {
        setShowFeedback(feedbackType);
      }
    }
  }, [chatId, chatInitialized, messages.length, sessions, showFeedback]);

  // Track message count for feedback logic
  useEffect(() => {
    if (messages.length > 0) {
      incrementMessageCount(messages.length);

      // Check if feedback should be shown after message update
      if (!feedbackDismissedRef.current && !showFeedback) {
        const feedbackType = shouldShowFeedback(messages.length);
        if (feedbackType) {
          setShowFeedback(feedbackType);
        }
      }
    }
  }, [messages.length, showFeedback]);

  // Handle feedback submission
  const handleFeedbackSubmit = useCallback(async (feedback: FeedbackData) => {
    try {
      await submitFeedbackApi({
        ...feedback,
        chatId: chatId || undefined,
        messageCount: messages.length,
        userId: user?.id,
      });
      recordFeedbackGiven();
      setShowFeedback(null);
      feedbackDismissedRef.current = true;
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    }
  }, [chatId, messages.length, user?.id]);

  // Handle feedback dismiss
  const handleFeedbackDismiss = useCallback(() => {
    setShowFeedback(null);
    feedbackDismissedRef.current = true;
  }, []);

  // Find selected message from URL messageId
  const selectedMessage = useMemo(() => {
    if (!messageId || !activeSession) return null;
    return activeSession.messages.find(m => m.id === messageId) || null;
  }, [messageId, activeSession]);

  // Handle message click - open detail panel (updates URL)
  const handleMessageClick = useCallback((message: ChatMessage) => {
    openMessageModal(message.id);
  }, [openMessageModal]);

  // Handle fast action on message (opens modal via URL)
  const handleMessageAction = useCallback((action: string, message: ChatMessage) => {
    console.log(`FastAction: ${action} on message:`, message.id);
    openMessageModal(message.id);
  }, [openMessageModal]);

  // Create new chat - navigate to /home (new chat screen)
  const handleNewChat = useCallback(() => {
    navigateToNewChat();
    textareaRef.current?.focus();
  }, [navigateToNewChat]);

  // Start editing title
  const handleStartEditTitle = useCallback(() => {
    setEditedTitle(activeSession?.title || '');
    setIsEditingTitle(true);
    setTimeout(() => titleInputRef.current?.focus(), 50);
  }, [activeSession?.title]);

  // Save edited title
  const handleSaveTitle = useCallback(async () => {
    if (chatId && editedTitle.trim()) {
      await updateSessionTitle(chatId, editedTitle.trim());
    }
    setIsEditingTitle(false);
  }, [chatId, editedTitle, updateSessionTitle]);

  // Cancel editing title
  const handleCancelEditTitle = useCallback(() => {
    setIsEditingTitle(false);
    setEditedTitle('');
  }, []);

  // Handle title input keydown
  const handleTitleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveTitle();
    } else if (e.key === 'Escape') {
      handleCancelEditTitle();
    }
  }, [handleSaveTitle, handleCancelEditTitle]);

  // Create folder
  const handleCreateFolder = useCallback(async () => {
    if (!newFolderName.trim()) return;
    await createFolder(newFolderName.trim());
    setNewFolderName('');
    setFolderModalOpen(false);
    showToast(t('sidebar.folderCreated'));
  }, [createFolder, newFolderName, showToast, t]);

  // Toggle folder expansion
  const toggleFolder = useCallback((folderId: string) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  }, []);

  // Chat actions
  const handleDeleteChat = useCallback(async (sessionId: string) => {
    await deleteSession(sessionId);
    setChatMenuOpen(null);
    showToast(t('sidebar.chatDeleted'));
  }, [deleteSession, showToast, t]);

  const handleArchiveChat = useCallback(async (sessionId: string) => {
    await archiveSession(sessionId);
    setChatMenuOpen(null);
    showToast(t('sidebar.chatArchived'));
  }, [archiveSession, showToast, t]);

  const handleUnarchiveChat = useCallback(async (sessionId: string) => {
    await unarchiveSession(sessionId);
    setChatMenuOpen(null);
    showToast(t('sidebar.chatRestored'));
  }, [unarchiveSession, showToast, t]);

  const handleDuplicateChat = useCallback(async (sessionId: string) => {
    await duplicateSession(sessionId);
    setChatMenuOpen(null);
    showToast(t('sidebar.chatDuplicated'));
  }, [duplicateSession, showToast, t]);

  const handleStartRenameChat = useCallback((sessionId: string, currentTitle: string) => {
    setRenamingChatId(sessionId);
    setRenamingChatTitle(currentTitle);
    setChatMenuOpen(null);
  }, []);

  const handleSaveRenameChat = useCallback(async () => {
    if (renamingChatId && renamingChatTitle.trim()) {
      await updateSessionTitle(renamingChatId, renamingChatTitle.trim());
      showToast(t('sidebar.chatRenamed'));
    }
    setRenamingChatId(null);
    setRenamingChatTitle('');
  }, [renamingChatId, renamingChatTitle, updateSessionTitle, showToast, t]);

  const handleCreateFromSummary = useCallback(async (sessionId: string) => {
    const session = sessions.find(s => s.id === sessionId);
    if (!session) return;

    // Create summary of the conversation
    const summary = session.messages
      .slice(0, 5)
      .map(m => `${m.role === 'user' ? 'Utente' : 'AI'}: ${m.content.slice(0, 100)}...`)
      .join('\n');

    const newId = await createSession(`Continuation of: ${session.title}`);
    await addMessage(newId, {
      role: 'user',
      content: `This is a continuation of the chat "${session.title}". Here is a summary:\n\n${summary}\n\nLet's continue from here.`,
    });
    setChatMenuOpen(null);
    showToast('New chat created from summary');
  }, [sessions, createSession, addMessage, showToast]);

  const handleMoveToFolder = useCallback(async (sessionId: string, folderId: string | null) => {
    await moveSessionToFolder(sessionId, folderId);
    setChatMenuOpen(null);
    showToast(folderId ? t('sidebar.chatMoved') : t('sidebar.chatRemovedFromFolder'));
  }, [moveSessionToFolder, showToast, t]);

  // Handle drawer item reorder (drag and drop)
  const handleDrawerReorder = useCallback((event: DrawerReorderEvent) => {
    const { draggedId, targetId, position } = event;

    // Handle drop on archive
    if (targetId === 'archive' && position === 'inside') {
      archiveSession(draggedId);
      showToast(t('sidebar.chatArchived'));
      return;
    }

    // Handle drop on folder
    if (position === 'inside') {
      // Check if target is a folder
      const folder = folders.find(f => f.id === targetId);
      if (folder) {
        moveSessionToFolder(draggedId, targetId);
        showToast(t('sidebar.chatMoved'));
        return;
      }
    }

    // Handle reorder (above/below)
    setChatOrder(prevOrder => {
      // Get all current session IDs that aren't archived
      const activeSessions = sessions.filter(s => !s.archived && !s.folderId);
      const allIds = activeSessions.map(s => s.id);

      // Start with existing order, add any new IDs
      let newOrder = [...prevOrder];
      allIds.forEach(id => {
        if (!newOrder.includes(id)) {
          newOrder.push(id);
        }
      });
      // Remove IDs that no longer exist
      newOrder = newOrder.filter(id => allIds.includes(id));

      // Find positions
      const draggedIndex = newOrder.indexOf(draggedId);
      let targetIndex = newOrder.indexOf(targetId);

      if (draggedIndex === -1 || targetIndex === -1) {
        return prevOrder;
      }

      // Remove dragged item
      newOrder.splice(draggedIndex, 1);

      // Recalculate target index after removal
      targetIndex = newOrder.indexOf(targetId);

      // Insert at new position
      if (position === 'below') {
        newOrder.splice(targetIndex + 1, 0, draggedId);
      } else {
        newOrder.splice(targetIndex, 0, draggedId);
      }

      // Save to localStorage
      localStorage.setItem('chat-order', JSON.stringify(newOrder));

      showToast(t('sidebar.orderUpdated'));
      return newOrder;
    });
  }, [sessions, folders, archiveSession, moveSessionToFolder, showToast, t]);

  // Set up drawer reorder callback
  useEffect(() => {
    setOnDrawerReorder(() => handleDrawerReorder);
    return () => setOnDrawerReorder(null);
  }, [handleDrawerReorder, setOnDrawerReorder]);

  // Check AI services on mount
  useEffect(() => {
    async function checkServices() {
      const [aiHealth, whisperOk] = await Promise.all([
        checkAiHealth(),
        whisperClient.checkHealth(),
      ]);
      setGroqAvailable(aiHealth.available);
      setWhisperAvailable(whisperOk);
    }
    checkServices();
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingText]);

  // Focus textarea on mount
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  // Set up sidebar with chat history
  useEffect(() => {
    const formatDate = (isoDate: string) => {
      const date = new Date(isoDate);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffDays === 0) return t('common.today');
      if (diffDays === 1) return t('common.yesterday');
      if (diffDays < 7) return t('common.daysAgo', { count: diffDays });
      return date.toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
    };

    // Filter and sort sessions (exclude archived unless viewing archive)
    let filteredSessions = [...sessions]
      .filter(s => showArchived ? s.archived : !s.archived);

    // Apply custom order if available, otherwise sort by updatedAt
    if (chatOrder.length > 0 && !showArchived) {
      filteredSessions.sort((a, b) => {
        const aIndex = chatOrder.indexOf(a.id);
        const bIndex = chatOrder.indexOf(b.id);
        // Items in custom order come first, sorted by their order
        // Items not in order come after, sorted by updatedAt
        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex;
        }
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      });
    } else {
      filteredSessions.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    }

    // Apply search filter if any
    if (sidebarSearchQuery.trim()) {
      const query = sidebarSearchQuery.toLowerCase();
      filteredSessions = filteredSessions.filter(s =>
        s.title.toLowerCase().includes(query) ||
        s.messages.some(m => m.content.toLowerCase().includes(query))
      );
    }

    // Get archived count
    const archivedCount = sessions.filter(s => s.archived).length;

    // Separate sessions by folder
    const rootSessions = filteredSessions.filter(s => !s.folderId).slice(0, 30);

    const drawerSections: any[] = [];

    // Section 1: Organize
    drawerSections.push({
      title: t('sidebar.organize'),
      items: [
        {
          id: 'archive',
          label: showArchived ? t('sidebar.backToChats') : t('sidebar.archive'),
          icon: 'archive',
          count: showArchived ? undefined : archivedCount,
          onClick: () => setShowArchived(!showArchived),
          isDropTarget: true, // Accept drops to archive
          draggable: false,
        },
        {
          id: 'create-folder',
          label: t('sidebar.createFolder'),
          icon: 'folder-plus',
          onClick: () => setFolderModalOpen(true),
          draggable: false,
        },
      ],
    });

    // Handler for fast actions on drawer items (defined early for reuse)
    const handleDrawerFastAction = (action: string, itemId: string) => {
      console.log(`DrawerFastAction: ${action} on chat: ${itemId}`);
      // Navigate to the chat and open modal for action
      navigateToChat(itemId);
      showToast(`Azione: ${action}`);
    };

    // Helper to create innerActions for a chat session (sizes +20%)
    const createChatInnerActions = (session: typeof sessions[0]) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <PrimaryAction type="star" active={false} size={17} />
        <EntityAvatar
          items={{ id: session.id, name: session.title || 'Chat', type: 'chat' }}
          type="chat"
          size={22}
        />
      </div>
    );

    // Section 2: Folders (if any and not showing archived)
    if (folders.length > 0 && !showArchived) {
      const folderItems = folders
        .sort((a, b) => a.order - b.order)
        .map(folder => {
          const folderSessions = filteredSessions.filter(s => s.folderId === folder.id);
          const isExpanded = expandedFolders.has(folder.id);

          return {
            id: folder.id,
            label: folder.name,
            icon: 'folder',
            count: folderSessions.length,
            active: isExpanded,
            onClick: () => toggleFolder(folder.id),
            isDropTarget: true, // Folders accept drops
            itemType: 'folder' as const,
            // Sub-items (chats in folder) - shown if expanded
            children: isExpanded ? folderSessions.map(session => ({
              id: session.id,
              label: session.title || t('sidebar.newChat'),
              hint: formatDate(session.updatedAt),
              active: session.id === chatId,
              onClick: () => navigateToChat(session.id),
              // Standard row model: inner actions + fast actions (lightning bolt trigger)
              innerActions: createChatInnerActions(session),
              showFastActions: true,
              onFastAction: handleDrawerFastAction,
              itemType: 'chat' as const,
            })) : undefined,
          };
        });

      if (folderItems.length > 0) {
        drawerSections.push({
          title: t('sidebar.folders'),
          items: folderItems,
        });
      }
    }

    // Helper to create actions for a chat
    const createChatActions = (session: typeof sessions[0]) => {
      const actions: any[] = [
        {
          id: 'new-from-summary',
          label: t('common.newFromSummary'),
          icon: 'message-circle',
          onClick: () => handleCreateFromSummary(session.id),
        },
        {
          id: 'rename',
          label: t('common.rename'),
          icon: 'pencil',
          onClick: () => handleStartRenameChat(session.id, session.title),
        },
        {
          id: 'duplicate',
          label: t('common.duplicate'),
          icon: 'copy',
          onClick: () => handleDuplicateChat(session.id),
        },
      ];

      // Add archive/unarchive action
      if (session.archived) {
        actions.push({
          id: 'unarchive',
          label: t('common.restore'),
          icon: 'archive',
          onClick: () => handleUnarchiveChat(session.id),
        });
      } else {
        actions.push({
          id: 'archive',
          label: t('common.archive'),
          icon: 'archive',
          onClick: () => handleArchiveChat(session.id),
        });
      }

      // Add folder move options
      if (folders.length > 0 && !showArchived) {
        if (session.folderId) {
          actions.push({
            id: 'remove-from-folder',
            label: t('common.removeFromFolder'),
            icon: 'folder',
            onClick: () => handleMoveToFolder(session.id, null),
          });
        }
        folders.forEach(folder => {
          if (folder.id !== session.folderId) {
            actions.push({
              id: `move-to-${folder.id}`,
              label: `${t('common.moveToFolder')}: ${folder.name}`,
              icon: 'folder',
              onClick: () => handleMoveToFolder(session.id, folder.id),
            });
          }
        });
      }

      // Delete action (always last, with danger styling)
      actions.push({
        id: 'delete',
        label: t('common.delete'),
        icon: 'trash',
        danger: true,
        onClick: () => handleDeleteChat(session.id),
      });

      return actions;
    };

    // Section 3: Recent chats (or Archived)
    drawerSections.push({
      title: showArchived ? t('sidebar.archivedChats') : t('sidebar.recentChats'),
      items: rootSessions.length > 0
        ? rootSessions.map(session => ({
            id: session.id,
            label: session.title || t('sidebar.newChat'),
            hint: formatDate(session.updatedAt),
            active: session.id === chatId,
            onClick: () => navigateToChat(session.id),
            actions: createChatActions(session),
            // Standard row model: inner actions + fast actions (lightning bolt trigger)
            innerActions: createChatInnerActions(session),
            showFastActions: true,
            onFastAction: handleDrawerFastAction,
            itemType: 'chat' as const,
          }))
        : [{ id: 'empty', label: sidebarSearchQuery ? t('common.noResults') : (showArchived ? t('sidebar.noArchivedChats') : t('sidebar.noChats')), hint: showArchived ? '' : t('sidebar.startConversation') }],
    });

    setDrawerSectionsOverride(drawerSections);
    // No cleanup needed - AppShell resets sections on view change
  }, [sessions, folders, chatId, navigateToChat, setDrawerSectionsOverride, sidebarSearchQuery, showArchived, expandedFolders, toggleFolder, handleCreateFromSummary, handleStartRenameChat, handleDuplicateChat, handleArchiveChat, handleUnarchiveChat, handleMoveToFolder, handleDeleteChat, chatOrder]);

  // Initialize AI tools and agents on mount
  useEffect(() => {
    initializeTools();
    initializeAgents();
  }, []);

  // Build system prompt with tools (ReAct pattern) + 4-Layer Memory
  const buildMartaSystemPrompt = useCallback(async (): Promise<string> => {
    // If user is logged in, use 4-layer memory system
    if (user?.id) {
      try {
        console.log('[Memory] Compiling 4-layer context for user:', user.id);
        const memoryPrompt = await buildSystemPromptWithMemory(user.id, {
          includeAllTools: true,
          includeDateTime: true,
          compact: false,
        });
        console.log('[Memory] Context compiled successfully');
        return memoryPrompt;
      } catch (memoryError) {
        console.warn('[Memory] Failed to compile memory context, using default:', memoryError);
        // Fallback to default prompt
      }
    }

    // Fallback: Use default system prompt without memory layers
    return buildSystemPrompt({
      includeAllTools: true,
      includeDateTime: true,
      compact: false,
    });
  }, [user?.id]);

  // Build enhanced system prompt with web search results
  const buildSearchEnhancedPromptLocal = useCallback(async (searchResults: SearchResponse): Promise<string> => {
    const basePrompt = await buildMartaSystemPrompt();
    const resultsSection = searchResults.results
      .map((r, i) => `${i + 1}. **${r.title}**\n   ${r.snippet}\n   Fonte: ${r.link}`)
      .join('\n\n');

    return `${basePrompt}

## WEB SEARCH RESULTS
${resultsSection}

Use this information to answer the user's question. Cite sources when using specific information.`;
  }, [buildMartaSystemPrompt]);

  // Generate chat title using AI
  const generateChatTitle = useCallback(async (sessionId: string, userMessage: string) => {
    if (!groqAvailable) return;

    try {
      const titlePrompt: GroqMessage[] = [
        {
          role: 'system',
          content: `Generate a short title (max 50 characters) for this conversation.
Reply ONLY with the title, without quotes or trailing punctuation.
The title should be descriptive.`,
        },
        {
          role: 'user',
          content: `User message: "${userMessage}"`,
        },
      ];

      // Use AI funnel for fast title generation (model selected by orchestrator)
      const response = await aiFunnelClient.chat(titlePrompt);
      let title = response.choices[0]?.message?.content || '';

      // Clean up title
      title = title.trim().replace(/^["']|["']$/g, '').slice(0, 60);

      if (title) {
        await updateSessionTitle(sessionId, title);
      }
    } catch (error) {
      console.error('Error generating chat title:', error);
      // Fallback: use first 40 chars of user message
      const fallbackTitle = userMessage.slice(0, 40) + (userMessage.length > 40 ? '...' : '');
      await updateSessionTitle(sessionId, fallbackTitle);
    }
  }, [groqAvailable, updateSessionTitle]);

  // Detect and record patterns from conversation (for collective learning)
  const detectAndRecordPatterns = useCallback(async (
    userMessage: string,
    assistantResponse: string,
    userId: string
  ) => {
    // Simple pattern detection heuristics
    // In production, this could use AI to analyze patterns more sophisticatedly

    const patterns: Array<{
      type: AIPatternType;
      key: string;
      value: string;
    }> = [];

    // 1. Detect terminology patterns (user uses specific terms)
    const terminologyMatches = userMessage.match(/[""]([^""]+)[""]/g);
    if (terminologyMatches) {
      terminologyMatches.forEach(match => {
        const term = match.replace(/[""]|[""]/g, '');
        if (term.length > 3 && term.length < 50) {
          patterns.push({
            type: 'terminology',
            key: term.toLowerCase(),
            value: `User uses the term "${term}"`,
          });
        }
      });
    }

    // 2. Detect workflow patterns (sequences of actions)
    const workflowKeywords = ['prima', 'poi', 'dopo', 'infine', 'quindi', 'successivamente'];
    const hasWorkflow = workflowKeywords.some(kw => userMessage.toLowerCase().includes(kw));
    if (hasWorkflow && userMessage.length > 50) {
      patterns.push({
        type: 'workflow',
        key: 'sequential_task',
        value: userMessage.substring(0, 200),
      });
    }

    // 3. Detect pricing patterns (mentions of prices/costs)
    const priceMatch = userMessage.match(/(\d+(?:[.,]\d{2})?)\s*€/);
    if (priceMatch) {
      patterns.push({
        type: 'pricing_pattern',
        key: 'price_reference',
        value: `Price mentioned: ${priceMatch[0]}`,
      });
    }

    // 4. Detect scheduling patterns (time preferences)
    const timePatterns = ['mattina', 'pomeriggio', 'sera', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì'];
    const timeMatch = timePatterns.find(tp => userMessage.toLowerCase().includes(tp));
    if (timeMatch) {
      patterns.push({
        type: 'scheduling',
        key: 'time_preference',
        value: `User mentions ${timeMatch}`,
      });
    }

    // Record detected patterns (they'll go to pending review queue)
    for (const pattern of patterns) {
      try {
        await recordPattern({
          sourceType: 'conversation',
          patternType: pattern.type,
          patternKey: pattern.key,
          patternValue: pattern.value,
          contextDescription: `From conversation: "${userMessage.substring(0, 100)}..."`,
        }, userId);
        console.log('[Pattern] Recorded:', pattern.type, pattern.key);
      } catch (err) {
        // Non-blocking, just log
        console.warn('[Pattern] Failed to record:', err);
      }
    }

    return patterns.length;
  }, []);

  // Send message to AI with ReAct tool calling pattern
  const sendToAI = useCallback(async (text: string, isTranscribed: boolean = false) => {
    if (!text.trim() || isProcessing) return;
    if (!groqAvailable) {
      showToast(t('errors.aiUnavailable'));
      return;
    }

    setIsProcessing(true);
    setStatus("streaming");

    // Handle "chat not found" case - user is describing a deleted chat
    let sessionId = chatId;
    let isNewSession = !sessionId || chatNotFound;
    let continuationContext = '';

    if (chatNotFound && chatNotFoundId) {
      // User is describing a deleted chat - create a new session with context
      console.log('[Chat] Creating continuation from deleted chat:', chatNotFoundId);
      sessionId = await createSession(`Continuation of deleted chat`);

      // Prepare context for the AI about the continuation
      continuationContext = `[CONTEXT: The user is continuing a previous conversation that was deleted. They described the content of the previous chat as follows:]\n\n`;

      // Clear the not found state
      setChatNotFound(false);
      setChatNotFoundId(null);

      // Navigate to the new chat URL
      navigateToChat(sessionId);
    } else if (!sessionId) {
      // Normal new chat
      sessionId = await createSession(text);
      // Navigate to the new chat URL
      navigateToChat(sessionId);
    }

    // Capture current attachments and clear pending
    const messageAttachments = [...pendingAttachments];
    setPendingAttachments([]);

    // Add user message with attachments
    console.log('[Chat] Messaggio inviato:', text.trim());
    await addMessage(sessionId, {
      role: "user",
      content: text.trim(),
      transcribed: isTranscribed,
      attachments: messageAttachments.length > 0 ? messageAttachments : undefined,
    });

    // Clear input
    setInputText('');

    try {
      // === AI ROUTER: Analyze message and decide provider/model ===
      let selectedModel = 'llama-3.1-8b-instant'; // Default Groq model

      try {
        const aiRouter = getAIRouter();
        const routingResult = await aiRouter.route(text.trim());

        // Log routing decision to console (the router already logs detailed pipeline)
        console.log('[AI Router] Decision:', {
          provider: routingResult.decision.provider,
          model: routingResult.decision.model,
          complexity: routingResult.analysis.complexity,
          confidence: routingResult.decision.confidence,
        });

        // Use the router's model decision (AI funnel handles provider routing)
        selectedModel = routingResult.decision.model as string || 'llama-3.1-8b-instant';
      } catch (routerError) {
        console.error('[AI Router] Error, using default model:', routerError);
        // Continue with default model
      }

      // Build base system prompt
      let systemPrompt = await buildMartaSystemPrompt();
      let searchResultsId: string | undefined;

      // Web search mode (optional)
      if (webSearchMode) {
        try {
          showToast(`🔍 ${t('ai.webSearching')}`);
          const savedSearch = await webSearchService.searchAndSave(text.trim(), 5);
          if (savedSearch.results.length > 0) {
            systemPrompt = await buildSearchEnhancedPromptLocal(savedSearch);
            searchResultsId = savedSearch.fileId;
            showToast(`✓ ${t('ai.webSearchResults', { count: savedSearch.results.length })}`);
          } else {
            showToast(`⚠️ ${t('ai.webSearchNoResults')}`);
          }
        } catch (searchError) {
          console.error('Web search error:', searchError);
          showToast(`⚠️ ${t('ai.webSearchFailed')}`);
        } finally {
          setWebSearchMode(false);
        }
      }

      // === BACKEND ORCHESTRATOR: Cloud AI intent detection (privacy-first) ===
      // Backend sanitizes the message and calls GPT/Claude for intent
      // Cloud AI sees ONLY placeholders, never real data
      let backendIntent: { category: string; action: string; mutation?: string; query?: string } | null = null;

      try {
        // ========================================================================
        // PRIVACY LOG - STEP 1: Original message sent by user
        // ========================================================================
        console.log('%c=== PRIVACY VERIFICATION LOG ===', 'background: #1e40af; color: white; padding: 4px 8px; font-weight: bold;');
        console.log('%c1. ORIGINAL MESSAGE:', 'color: #dc2626; font-weight: bold;');
        console.log(`   "${text.trim()}"`);

        const orchestrateResult = await backendOrchestrate({
          message: text.trim(),
          currentView: 'home',
        });

        // ========================================================================
        // PRIVACY LOG - STEP 2: Sanitized message sent to Cloud AI
        // ========================================================================
        console.log('%c2. SANITIZED MESSAGE (sent to Cloud AI):', 'color: #16a34a; font-weight: bold;');
        if (orchestrateResult.sanitizedMessage) {
          console.log(`   "${orchestrateResult.sanitizedMessage}"`);
          if (orchestrateResult.piiDetected.length > 0) {
            console.log('%c   PII DETECTED AND MASKED:', 'color: #ea580c;', orchestrateResult.piiDetected.join(', '));
          } else {
            console.log('   (No PII detected in message)');
          }
        } else {
          console.log('   (Message not available - possible error or cache)');
        }

        // ========================================================================
        // PRIVACY LOG - STEP 3: Response received from AI
        // ========================================================================
        console.log('%c3. AI RESPONSE:', 'color: #7c3aed; font-weight: bold;');
        console.log(`   Model: ${orchestrateResult.modelUsed || 'N/A'} | Tier: ${orchestrateResult.complexityTier || 'N/A'}`);
        console.log(`   Intent: ${orchestrateResult.intent?.category || 'N/A'}:${orchestrateResult.intent?.action || 'N/A'}`);
        console.log(`   From Cache: ${orchestrateResult.fromCache ? 'YES' : 'NO'}`);
        if (orchestrateResult.error) {
          console.log('%c   ERROR:', 'color: #dc2626;', orchestrateResult.error);
        }

        if (orchestrateResult.success && orchestrateResult.intent) {
          backendIntent = {
            category: orchestrateResult.intent.category,
            action: orchestrateResult.intent.action,
            mutation: orchestrateResult.intent.mutation ?? undefined,
            query: orchestrateResult.intent.query ?? undefined,
          };

          // ========================================================================
          // PRIVACY LOG - STEP 4: DB Operation (if detected)
          // ========================================================================
          if (orchestrateResult.intent.mutation || orchestrateResult.intent.query) {
            console.log('%c4. DB OPERATION DETECTED:', 'color: #0891b2; font-weight: bold;');
            if (orchestrateResult.intent.mutation) {
              console.log(`   Mutation: ${orchestrateResult.intent.mutation}`);
            }
            if (orchestrateResult.intent.query) {
              console.log(`   Query: ${orchestrateResult.intent.query}`);
            }
            console.log(`   Required entities: ${orchestrateResult.intent.requiredEntities.join(', ') || 'none'}`);
          }
        } else if (orchestrateResult.error) {
          console.warn('[Orchestrator] Backend error, falling back to local:', orchestrateResult.error);
        }

        console.log('%c================================', 'background: #1e40af; color: white; padding: 4px 8px;');
      } catch (orchestrateError) {
        console.warn('[Orchestrator] Backend unavailable, using local runner:', orchestrateError);
      }

      // === RUNNER: Preprocess query and fetch data from DB ===
      // Use cloud intent (if available) to guide data retrieval
      const aiRunner = await getAIRunner();
      const preprocessResult = await aiRunner.preprocessQuery(text.trim());
      console.log('[Runner] Local Intent:', preprocessResult.intent.domain, preprocessResult.intent.operation);

      // Se abbiamo un intent dal cloud, logga la comparazione
      if (backendIntent) {
        console.log('[Intent Compare] Cloud:', backendIntent.category, '| Local:', preprocessResult.intent.domain);
      }

      // Inject real data into system prompt
      if (preprocessResult.contextInjection) {
        systemPrompt = aiRunner.buildPromptWithData(systemPrompt, preprocessResult.contextInjection);
        console.log('[Runner] Data injected into prompt');
        if (preprocessResult.fetchedData) {
          showToast(`📊 ${preprocessResult.fetchedData.count} ${preprocessResult.intent.domain} dal database`);
        }
      }

      // If cloud detected a specific mutation/query, add it to context
      if (backendIntent?.mutation || backendIntent?.query) {
        const actionInfo = backendIntent.mutation
          ? `Mutation suggerita: ${backendIntent.mutation}`
          : `Query suggerita: ${backendIntent.query}`;
        systemPrompt += `\n\n## AZIONE RILEVATA\n${actionInfo}\nCategoria: ${backendIntent.category}, Azione: ${backendIntent.action}`;
      }

      // Build conversation history
      const freshSessions = useChatStore.getState().sessions;
      const allSessionMessages = freshSessions.find(s => s.id === sessionId)?.messages || [];
      const previousMessages = allSessionMessages.slice(0, -1);

      const userMessageContent = continuationContext
        ? `${continuationContext}${text.trim()}`
        : text.trim();

      // Reset tool call count for this message
      resetToolCallCount();

      // Build initial conversation
      const chatMessages: GroqMessage[] = [
        { role: "system", content: systemPrompt },
        ...previousMessages.map((m) => ({
          role: m.role === "user" ? "user" : "assistant",
          content: m.content,
        } as GroqMessage)),
        { role: "user", content: userMessageContent },
      ];

      // ReAct loop: max 5 tool calls per message
      const MAX_TOOL_CALLS = 5;
      let currentToolCallCount = 0;
      let fullResponse = "";
      let finalResponse = "";

      while (currentToolCallCount < MAX_TOOL_CALLS) {
        // Get LLM response through AI funnel (model selected by orchestrator)
        // Using non-streaming chat() for reliable responses
        fullResponse = "";
        console.log('[Chat] Starting LLM request through AI funnel');
        console.log('[Chat] Messages being sent:', JSON.stringify(chatMessages.map(m => ({ role: m.role, contentLength: m.content.length }))));

        try {
          setStreamingText("Thinking...");
          const chatResponse = await aiFunnelClient.chat(chatMessages);
          console.log('[Chat] Raw response:', JSON.stringify(chatResponse, null, 2));
          fullResponse = chatResponse.choices[0]?.message?.content || "";
          setStreamingText(fullResponse);
          console.log('[Chat] Response received, length:', fullResponse.length, 'content:', fullResponse.substring(0, 200));
        } catch (chatError) {
          console.error('[Chat] Chat error:', chatError);
          throw chatError;
        }

        console.log('[Chat] Risposta LLM:', fullResponse.substring(0, 200) + '...');

        // Check if response contains a tool call
        const toolCall = parseToolCall(fullResponse);

        if (!toolCall) {
          // No tool call - this is the final response
          finalResponse = fullResponse;
          break;
        }

        console.log('[Tool] Detected tool call:', toolCall.tool, toolCall.parameters);
        incrementToolCallCount();
        currentToolCallCount++;
        setStatus("executing_tool");
        showToast(`🔧 ${t('ai.toolExecuting', { tool: toolCall.tool })}`);

        // Check if tool requires confirmation (DELETE operations)
        if (requiresConfirmation(toolCall.tool)) {
          console.log('[Tool] Confirmation required for:', toolCall.tool);
          const confirmation = createPendingConfirmation(toolCall);
          setPendingConfirmation(confirmation);

          // Ask for confirmation
          finalResponse = createConfirmationRequest(
            confirmation.entityType || 'elemento',
            confirmation.entityName || 'sconosciuto',
            'eliminare'
          );
          break;
        }

        // Execute the tool
        const toolResult = await executeToolCall(toolCall, { timeout: 30000 });
        console.log('[Tool] Result:', toolResult.success ? 'OK' : 'ERROR', toolResult);

        if (toolResult.success) {
          showToast(`✓ ${t('ai.toolCompleted', { tool: toolCall.tool })}`);
        } else {
          showToast(`⚠️ ${t('ai.toolError', { error: toolResult.error })}`);
        }

        // Inject the result into the conversation and continue
        const toolResultMessage = createToolResultInjection(
          toolCall.tool,
          toolResult.success,
          toolResult.success ? toolResult.data : toolResult.error
        );

        // Add assistant's tool call response and tool result to conversation
        chatMessages.push({ role: "assistant", content: fullResponse });
        chatMessages.push({ role: "user", content: toolResultMessage });

        setStatus("streaming");
      }

      if (currentToolCallCount >= MAX_TOOL_CALLS) {
        console.warn('[Tool] Max tool calls reached, stopping loop');
        finalResponse = fullResponse || t('ai.maxToolCalls');
      }

      console.log('[Chat] Risposta finale:', finalResponse.substring(0, 200) + '...');

      // Add assistant message
      console.log('%c[Chat] ADDING ASSISTANT MESSAGE', 'background: #059669; color: white;');
      console.log('   SessionId:', sessionId);
      console.log('   Content length:', finalResponse.length);
      console.log('   Content preview:', finalResponse.substring(0, 100));

      await addMessage(sessionId, {
        role: "assistant",
        content: finalResponse,
        searchResultsId,
      });

      console.log('%c[Chat] MESSAGE ADDED SUCCESSFULLY', 'background: #059669; color: white;');

      // Verify the message was added to state
      const updatedSessions = useChatStore.getState().sessions;
      const updatedSession = updatedSessions.find(s => s.id === sessionId);
      console.log('   Session messages count:', updatedSession?.messages.length);
      console.log('   Last message role:', updatedSession?.messages[updatedSession.messages.length - 1]?.role);

      setStreamingText("");
      setStatus("idle");

      // Generate title for new sessions after first exchange
      if (isNewSession && sessionId) {
        generateChatTitle(sessionId, text.trim());
      }

      // === PATTERN DETECTION (async, non-blocking) ===
      // Analyze the conversation for learnable patterns
      if (user?.id) {
        detectAndRecordPatterns(text.trim(), finalResponse, user.id).catch(err => {
          console.warn('[Pattern] Detection failed (non-blocking):', err);
        });
      }
    } catch (error) {
      console.error("AI error:", error);
      setError(error instanceof Error ? error.message : t('common.error'));
      showToast(t('errors.aiCommunication'));
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing, groqAvailable, chatId, navigateToChat, createSession, addMessage, setStreamingText, setStatus, setError, buildMartaSystemPrompt, buildSearchEnhancedPromptLocal, webSearchMode, showToast, generateChatTitle, pendingAttachments, chatNotFound, chatNotFoundId, incrementToolCallCount, resetToolCallCount, setPendingConfirmation, t]);

  // Handle confirmation for DELETE operations
  const handleConfirmation = useCallback(async (confirmed: boolean) => {
    if (!pendingConfirmation) return;

    const sessionId = chatId;
    if (!sessionId) return;

    if (confirmed) {
      setIsProcessing(true);
      setStatus("executing_tool");
      showToast(`🔧 ${t('ai.toolExecuting', { tool: pendingConfirmation.tool })}`);

      try {
        const toolResult = await executeToolCall({
          tool: pendingConfirmation.tool,
          parameters: pendingConfirmation.parameters,
        });

        if (toolResult.success) {
          showToast(`✓ ${t('ai.deleteConfirmed', { type: pendingConfirmation.entityType, name: pendingConfirmation.entityName })}`);
          await addMessage(sessionId, {
            role: "assistant",
            content: t('ai.deleteConfirmed', { type: pendingConfirmation.entityType, name: pendingConfirmation.entityName }),
          });
        } else {
          showToast(`⚠️ ${t('ai.toolError', { error: toolResult.error })}`);
          await addMessage(sessionId, {
            role: "assistant",
            content: t('ai.deleteFailed', { type: pendingConfirmation.entityType, error: toolResult.error }),
          });
        }
      } catch (error) {
        console.error("Tool execution error:", error);
        showToast(t('common.error'));
      } finally {
        setIsProcessing(false);
        setStatus("idle");
      }
    } else {
      const cancelMessage = createCancellationMessage(pendingConfirmation.entityType || 'element');
      await addMessage(sessionId, {
        role: "assistant",
        content: cancelMessage,
      });
      showToast(t('ai.deleteCancelled', { type: pendingConfirmation.entityType }));
    }

    setPendingConfirmation(null);
  }, [pendingConfirmation, chatId, addMessage, showToast, setStatus, setPendingConfirmation]);

  // Handle send
  const handleSend = useCallback(() => {
    if (!inputText.trim()) return;

    // Check if this is a confirmation/cancellation for pending operation
    if (pendingConfirmation) {
      const text = inputText.trim();
      if (isConfirmation(text)) {
        setInputText('');
        // Add user confirmation message
        if (chatId) {
          addMessage(chatId, { role: "user", content: text });
        }
        handleConfirmation(true);
        return;
      } else if (isCancellation(text)) {
        setInputText('');
        // Add user cancellation message
        if (chatId) {
          addMessage(chatId, { role: "user", content: text });
        }
        handleConfirmation(false);
        return;
      }
      // Not a clear confirmation/cancellation - treat as new message
      // Clear pending confirmation and proceed normally
      setPendingConfirmation(null);
    }

    sendToAI(inputText, false);
  }, [inputText, sendToAI, pendingConfirmation, handleConfirmation, chatId, addMessage, setPendingConfirmation]);

  // Handle key press
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  // Start recording
  const startRecording = useCallback(async () => {
    if (!whisperAvailable) {
      showToast(t('errors.whisperUnavailable'));
      return;
    }

    try {
      recorderRef.current = whisperClient.createAudioRecorder();
      await recorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);

      recordingTimerRef.current = window.setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Recording error:", error);
      showToast(t('errors.microphonePermission'));
    }
  }, [whisperAvailable, showToast, t]);

  // Stop recording, save, and transcribe
  const stopRecording = useCallback(async () => {
    if (!recorderRef.current) return;

    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }

    setIsRecording(false);
    setIsTranscribing(true);

    try {
      const audioBlob = await recorderRef.current.stop();
      recorderRef.current = null;

      // Transcribe the audio
      const transcription = await whisperClient.transcribeBlob(audioBlob, "it");

      setIsTranscribing(false);
      setIsProcessing(true);

      // Save audio to local storage with transcription
      const savedAudio = await fileStorageService.saveAudioRecording(audioBlob, transcription);
      console.log(`[HomeView] Audio saved: ${savedAudio.id} (${(savedAudio.size / 1024).toFixed(1)} KB)`);

      if (transcription.trim()) {
        // Create attachment for the audio
        const audioAttachment: ChatAttachment = {
          id: savedAudio.id,
          name: savedAudio.name,
          type: savedAudio.type,
          size: savedAudio.size,
        };
        setPendingAttachments(prev => [...prev, audioAttachment]);

        await sendToAI(transcription.trim(), true);
      } else {
        showToast(t('common.noResults'));
        setIsProcessing(false);
      }
    } catch (error) {
      console.error("Transcription error:", error);
      setIsTranscribing(false);
      showToast(t('errors.transcriptionFailed'));
      setIsProcessing(false);
    }
  }, [sendToAI, showToast, t]);

  // Toggle recording
  const toggleRecording = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  // Handle file attachment
  const handleFileAttach = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        showToast(`${t('common.loading')} ${file.name}...`);

        // Save file to local storage
        const storedFile = await fileStorageService.saveDocument(file);

        // Add to pending attachments
        const attachment: ChatAttachment = {
          id: storedFile.id,
          name: storedFile.name,
          type: storedFile.type,
          size: storedFile.size,
        };
        setPendingAttachments(prev => [...prev, attachment]);

        showToast(`✓ ${file.name}`);
      } catch (error) {
        console.error('Error saving file:', error);
        showToast(t('errors.fileSaveFailed'));
      }
    }
    e.target.value = '';
  }, [showToast, t]);

  // Format recording time
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const hasMessages = messages.length > 0 || streamingText;

  return (
    <div style={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'var(--background)',
      overflow: 'hidden',
    }}>
      {/* Messages area - scrollable only if there are messages */}
      <div className="home-messages" style={{
        flex: 1,
        overflowY: hasMessages ? 'auto' : 'hidden',
        paddingTop: hasMessages ? '1rem' : 0,
        paddingBottom: hasMessages ? '1rem' : 0,
        paddingLeft: '1rem',
        paddingRight: '1rem',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Chat not found state */}
        {chatNotFound && (
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            color: 'var(--text-muted)',
            padding: '1rem',
          }}>
            <Archive size={32} style={{ marginBottom: '0.5rem', opacity: 0.5, color: 'var(--warning, #f59e0b)' }} />
            <h2 style={{ fontSize: '1.125rem', marginBottom: '0.5rem', color: 'var(--text)' }}>
              {t('home.chatNotFound.title')}
            </h2>
            <p style={{ fontSize: '0.875rem', marginBottom: '1rem', color: 'var(--text-muted)', lineHeight: '1.5', maxWidth: '400px' }}>
              {t('home.chatNotFound.message')}
              <br />
              <span style={{ fontSize: '0.75rem', opacity: 0.7 }}>
                ID: {chatNotFoundId?.slice(0, 8)}...
              </span>
            </p>
            <p style={{ fontSize: '0.875rem', marginBottom: '1rem', color: 'var(--text)', fontWeight: 500 }}>
              {t('home.chatNotFound.describeToContinue')}
            </p>
            <button
              onClick={() => {
                setChatNotFound(false);
                setChatNotFoundId(null);
                navigateToNewChat();
              }}
              style={{
                padding: '0.5rem 1rem',
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                fontSize: '0.875rem',
                color: 'var(--text-muted)',
              }}
            >
              {t('home.chatNotFound.startNew')}
            </button>
          </div>
        )}

        {/* Welcome state - only show if no messages, not streaming, and chat exists (or no chatId) */}
        {messages.length === 0 && !streamingText && !chatNotFound && (
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            color: 'var(--text-muted)',
          }}>
            <Sparkles size={32} style={{ marginBottom: '0.5rem', opacity: 0.5 }} />
            <h2 style={{ fontSize: '1.125rem', marginBottom: '0.25rem', color: 'var(--text)' }}>
              {t('home.greeting')}
            </h2>
            <p style={{ fontSize: '0.75rem', marginBottom: '0.5rem', color: 'var(--text-muted)', lineHeight: '1.4' }}>
              {t('home.voiceTip')}
            </p>

            {/* Quick action buttons - compact for mobile */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '0.5rem',
              marginTop: '0.75rem',
              width: '100%',
              maxWidth: '400px',
            }}>
              {[
                { icon: '📄', text: t('home.quickActions.projectFromPdf') },
                { icon: '📅', text: t('home.quickActions.organizeWeek') },
                { icon: '✉️', text: t('home.quickActions.summarizeEmail') },
                { icon: '📊', text: t('home.quickActions.prepareReport') },
              ].map((action) => (
                <button
                  key={action.text}
                  onClick={() => setInputText(action.text)}
                  style={{
                    padding: '0.625rem 0.75rem',
                    background: 'var(--surface)',
                    border: '1px solid var(--border)',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    color: 'var(--text)',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#3b82f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border)';
                  }}
                >
                  {action.icon} {action.text}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Chat messages with proper layout */}
        {messages.map((msg) => (
          <ChatMessageBubble
            key={msg.id}
            message={msg}
            onClick={handleMessageClick}
            onAction={handleMessageAction}
            connectionCount={0}
            compact={true}
          />
        ))}

        {streamingText && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            marginBottom: '16px',
            maxWidth: '85%',
          }}>
            <div style={{
              padding: '10px 14px',
              borderRadius: '16px',
              borderBottomLeftRadius: '4px',
              background: '#f3f4f6',
              color: '#1f2937',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            }}>
              <div style={{
                fontSize: '14px',
                lineHeight: 1.5,
                wordBreak: 'break-word',
              }} className="markdown-assistant">
                <Markdown>{streamingText}</Markdown>
              </div>
              <span style={{
                display: 'block',
                fontSize: '11px',
                marginTop: '4px',
                textAlign: 'right',
                color: '#9ca3af',
              }}>
                {t('home.responding')}
              </span>
            </div>
          </div>
        )}

        {/* Transcription in progress indicator */}
        {isTranscribing && (
          <div style={{
            marginBottom: '1.5rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
          }}>
            <div style={{
              maxWidth: '80%',
              padding: '0.75rem 1rem',
              borderRadius: '1rem',
              backgroundColor: 'var(--primary, #3b82f6)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}>
              <Loader2 size={16} className="spin" />
              <span>{t('home.transcribing')}</span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Feedback Survey - appears based on usage patterns */}
      {showFeedback && (
        <div style={{
          position: 'absolute',
          bottom: '5rem',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '90%',
          maxWidth: '400px',
          zIndex: 20,
        }}>
          <FeedbackSurvey
            feedbackType={showFeedback}
            chatId={chatId || undefined}
            messageCount={messages.length}
            onSubmit={handleFeedbackSubmit}
            onDismiss={handleFeedbackDismiss}
          />
        </div>
      )}

      {/* Input area - fixed bottom when bars are hidden, otherwise in flow */}
      <div style={{
        position: barsHidden ? 'fixed' : 'relative',
        bottom: barsHidden ? 0 : 'auto',
        left: barsHidden ? '5%' : 0,
        right: barsHidden ? '5%' : 0,
        borderTop: '1px solid var(--border)',
        paddingTop: '0.75rem',
        paddingBottom: '0.75rem',
        paddingLeft: '1rem',
        paddingRight: '1rem',
        backgroundColor: 'var(--background)',
        zIndex: 10,
        transition: 'all 0.3s ease',
      }}>
        {/* Pending attachments */}
        {pendingAttachments.length > 0 && (
          <div style={{
            marginBottom: '0.75rem',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.5rem',
          }}>
            {pendingAttachments.map((attachment) => (
              <div
                key={attachment.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 0.75rem',
                  backgroundColor: 'var(--surface)',
                  borderRadius: '0.5rem',
                  border: '1px solid var(--border)',
                  fontSize: '0.875rem',
                }}
              >
                <Paperclip size={14} style={{ color: 'var(--primary, #3b82f6)' }} />
                <span style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {attachment.name}
                </span>
                <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                  ({(attachment.size / 1024).toFixed(1)} KB)
                </span>
                <button
                  onClick={() => setPendingAttachments(prev => prev.filter(a => a.id !== attachment.id))}
                  style={{
                    padding: '0.125rem',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    color: 'var(--text-muted)',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Web search prompt */}
        {webSearchMode && (
          <div style={{
            marginBottom: '0.75rem',
            padding: '0.75rem',
            backgroundColor: 'var(--surface)',
            borderRadius: '0.75rem',
            border: '1px solid var(--primary, #3b82f6)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}>
            <Globe size={16} style={{ color: 'var(--primary, #3b82f6)' }} />
            <input
              type="text"
              placeholder="Additional prompt to import web results..."
              value={webSearchPrompt}
              onChange={(e) => setWebSearchPrompt(e.target.value)}
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                backgroundColor: 'transparent',
                color: 'var(--text)',
                fontSize: '0.875rem',
              }}
            />
          </div>
        )}

        {/* Input box - no borders, touches 90% margins */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
        }}>
          <textarea
            ref={textareaRef}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isRecording ? t('home.inputPlaceholderVoice') : webSearchMode ? t('home.inputPlaceholderWebSearch') : t('home.inputPlaceholder')}
            disabled={isProcessing || isRecording}
            rows={2}
            style={{
              width: '100%',
              border: 'none',
              outline: 'none',
              resize: 'none',
              fontSize: '1rem',
              backgroundColor: 'var(--surface, #f5f5f5)',
              color: 'var(--text)',
              padding: '0.75rem',
              borderRadius: '0.5rem',
            }}
          />
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                ref={fileInputRef}
                type="file"
                hidden
                onChange={handleFileChange}
              />
              <button
                onClick={handleFileAttach}
                disabled={isProcessing}
                style={{
                  padding: '0.5rem',
                  border: 'none',
                  background: 'none',
                  cursor: isProcessing ? 'not-allowed' : 'pointer',
                  color: 'var(--text-muted)',
                  display: 'flex',
                  alignItems: 'center',
                  opacity: isProcessing ? 0.5 : 1,
                }}
                aria-label="Attach file"
              >
                <Paperclip size={20} />
              </button>
              <button
                onClick={toggleRecording}
                disabled={isProcessing && !isRecording}
                style={{
                  padding: '0.5rem',
                  border: 'none',
                  background: isRecording ? '#ef4444' : 'none',
                  cursor: isProcessing && !isRecording ? 'not-allowed' : 'pointer',
                  color: isRecording ? 'white' : 'var(--text-muted)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  borderRadius: '0.5rem',
                  opacity: isProcessing && !isRecording ? 0.5 : 1,
                }}
                aria-label={isRecording ? 'Ferma registrazione' : 'Registra'}
              >
                {isRecording ? (
                  <>
                    <MicOff size={20} />
                    <span style={{ fontSize: '0.75rem' }}>{formatTime(recordingTime)}</span>
                  </>
                ) : (
                  <Mic size={20} />
                )}
              </button>
              <button
                onClick={() => setWebSearchMode(!webSearchMode)}
                style={{
                  padding: '0.5rem',
                  border: webSearchMode ? 'none' : '1px solid var(--border, #e5e7eb)',
                  background: webSearchMode ? 'var(--primary, #3b82f6)' : 'none',
                  cursor: 'pointer',
                  color: webSearchMode ? 'white' : 'var(--text-muted)',
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: '0.5rem',
                  transition: 'all 0.2s ease',
                  position: 'relative',
                }}
                aria-label={webSearchMode ? 'Web search active' : 'Enable web search'}
                title={webSearchMode ? '🌐 Web search ACTIVE' : 'Click to enable web search'}
              >
                <Globe size={20} />
                {webSearchMode && (
                  <span style={{
                    position: 'absolute',
                    top: '-4px',
                    right: '-4px',
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: '#10b981',
                    border: '2px solid white',
                  }} />
                )}
              </button>
            </div>
            <button
              onClick={handleSend}
              disabled={isProcessing || isRecording || !inputText.trim()}
              style={{
                padding: '0.5rem 1rem',
                border: 'none',
                backgroundColor: 'var(--primary, #3b82f6)',
                color: 'white',
                borderRadius: '0.5rem',
                cursor: isProcessing || isRecording || !inputText.trim() ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                opacity: isProcessing || isRecording || !inputText.trim() ? 0.5 : 1,
              }}
              aria-label={t('common.send')}
            >
              {isProcessing ? <Loader2 size={18} className="spin" /> : <Send size={18} />}
              <span>{isProcessing ? t('common.wait') : t('common.send')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Message Detail Panel */}
      <MessageDetailPanel
        message={selectedMessage}
        isOpen={isMessageModalOpen}
        onClose={closeMessageModal}
      />

      {/* Folder Creation Modal */}
      {folderModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setFolderModalOpen(false)}
        >
          <div
            style={{
              backgroundColor: 'var(--background, #fff)',
              borderRadius: '0.75rem',
              padding: '1.5rem',
              minWidth: '320px',
              maxWidth: '90vw',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 1rem', fontSize: '1.125rem', fontWeight: 600 }}>
              {t('sidebar.createFolderTitle')}
            </h3>
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder={t('sidebar.folderNamePlaceholder')}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateFolder();
                if (e.key === 'Escape') setFolderModalOpen(false);
              }}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid var(--border, #e5e7eb)',
                borderRadius: '0.5rem',
                fontSize: '1rem',
                marginBottom: '1rem',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button
                onClick={() => setFolderModalOpen(false)}
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid var(--border, #e5e7eb)',
                  borderRadius: '0.5rem',
                  background: 'none',
                  cursor: 'pointer',
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCreateFolder}
                disabled={!newFolderName.trim()}
                style={{
                  padding: '0.5rem 1rem',
                  border: 'none',
                  borderRadius: '0.5rem',
                  backgroundColor: 'var(--primary, #3b82f6)',
                  color: 'white',
                  cursor: newFolderName.trim() ? 'pointer' : 'not-allowed',
                  opacity: newFolderName.trim() ? 1 : 0.5,
                }}
              >
                {t('common.create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
